// ============================================================
// FONADHOO CRM v3.0 - ROUTING & LAYOUT
// Project 1 Layout + Project 2 Features
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { hasPermission, hasEffectivePermission } from './shared/constants';
import { getThemeMode, toggleThemeMode } from './shared/theme';
import { Icon } from './shared/Icon';
import { ProgressRing, SegmentDonut } from './shared/Charts';

// Import all feature components
import VoterProfile from './VoterProfile';
import HouseholdView from './HouseholdView';
import EnhancedOutreach from './EnhancedOutreach';
import RemindersPanel from './RemindersPanel';
import TeamDashboard from './TeamDashboard';
import Analytics from './Analytics';
import EventManagement from './EventManagement';
import CampaignTrips from './CampaignTrips';
import CampaignCalendar from './CampaignCalendar';
import BallotCount from './BallotCount';
import GOTVDashboard from './GOTVDashboard';
import AdvancedSearch from './AdvancedSearch';
import AddMemberModal2Step from './AddMemberModal2Step';

// Initialize Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Note: User create/delete now goes through Supabase Edge Functions
// (supabase/functions/create-user, delete-user) so the service role key
// is never shipped to the browser. Deploy with:
//   supabase functions deploy create-user --no-verify-jwt
//   supabase functions deploy delete-user --no-verify-jwt

// Light/dark toggle — used in the header and on the login page.
function ThemeToggle({ style }) {
  const [mode, setMode] = useState(getThemeMode);
  return (
    <button
      onClick={() => setMode(toggleThemeMode())}
      title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
      aria-label="Toggle light or dark theme"
      style={{
        padding: '7px',
        background: 'transparent',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        color: 'var(--text-2)',
        cursor: 'pointer',
        lineHeight: 0,
        flexShrink: 0,
        display: 'inline-flex',
        transition: 'color 0.15s, border-color 0.15s',
        ...style,
      }}
      onMouseEnter={e => { e.currentTarget.style.color = 'var(--link)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-2)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <Icon name={mode === 'light' ? 'moon' : 'sun'} size={16} />
    </button>
  );
}

// ══════════════════════════════════════════════════════════
// PROJECT 1 LAYOUT WRAPPER
// ══════════════════════════════════════════════════════════

function Project1Layout({ children, user, onSignOut, currentPage = 'home', onExport, onImportFile, onExportTree, onImportTreeFile, branding = {}, onEditBranding }) {
  const navigate = useNavigate();
  const importInputRef = React.useRef(null);
  const importTreeInputRef = React.useRef(null);

  // Responsive breakpoint — matches the rest of the app. Uses matchMedia
  // (not innerWidth) to stay stable on iOS Safari whose URL bar fires
  // spurious resize events on scroll.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  // Desktop shell breakpoint — sidebar app layout kicks in ≥1024px.
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(min-width: 1024px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  const navItems = [
    { key: 'home',       label: 'Home',       path: '/',           icon: 'home',     permission: 'canLogOutreach' },
    { key: 'search',     label: 'Search',     path: '/search',     icon: 'search',   permission: 'canLogOutreach' },
    { key: 'households', label: 'Households', path: '/households', icon: 'building', permission: 'canLogOutreach' },
    { key: 'outreach',   label: 'Outreach',   path: '/outreach',   icon: 'phone',    permission: 'canLogOutreach' },
    { key: 'reminders',  label: 'Reminders',  path: '/reminders',  icon: 'bell',     permission: 'canLogOutreach' },
    { key: 'events',     label: 'Events',     path: '/events',     icon: 'calendar', permission: 'canManageEvents' },
    { key: 'trips',      label: 'Trips',      path: '/trips',      icon: 'plane',    permission: 'canManageTrips' },
    { key: 'calendar',   label: 'Calendar',   path: '/calendar',   icon: 'calendarRange', permission: 'canManageEvents' },
    { key: 'gotv',       label: 'GOTV',       path: '/gotv',       icon: 'vote',     permission: 'canViewVotes' },
    { key: 'count',      label: 'Count',      path: '/count',      icon: 'pie',      permission: 'canViewVotes' },
    { key: 'team',       label: 'Team',       path: '/team',       icon: 'users',    permission: 'canManageUsers' },
    { key: 'analytics',  label: 'Analytics',  path: '/analytics',  icon: 'chart',    permission: 'canViewAnalytics' },
  ].filter(item => !item.permission || hasEffectivePermission(user, item.permission));

  const PAGE_TITLES = {
    home: 'Dashboard', search: 'Search', households: 'Households', outreach: 'Outreach',
    reminders: 'Reminders', events: 'Events', trips: 'Trips', calendar: 'Calendar', gotv: 'GOTV',
    count: 'Count', team: 'Team', analytics: 'Analytics',
  };
  const activeNav = navItems.find(n => n.key === currentPage);

  // ─── DESKTOP SIDEBAR APP SHELL (≥1024px) ───────────────────────────
  if (isDesktop) {
    const canExp = hasEffectivePermission(user, 'canExport');
    const tool = (iconName, label, onClick) => (
      <button onClick={onClick} title={label} aria-label={label}
        style={{ width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center',
          background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-2)',
          cursor: 'pointer', transition: 'border-color 0.15s, color 0.15s', flexShrink: 0 }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}>
        <Icon name={iconName} size={16} />
      </button>
    );

    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex' }}>
        {/* Hidden file inputs (shared with topbar tools) */}
        <input ref={importInputRef} type="file" accept=".csv" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f && onImportFile) onImportFile(f); e.target.value = ''; }} />
        <input ref={importTreeInputRef} type="file" accept=".json" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f && onImportTreeFile) onImportTreeFile(f); e.target.value = ''; }} />

        {/* ── SIDEBAR ── */}
        <aside style={{
          width: 248, flexShrink: 0, position: 'sticky', top: 0, height: '100vh',
          background: 'var(--surface)', borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', padding: '18px 14px',
        }}>
          {/* Brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '4px 8px 18px', borderBottom: '1px solid var(--border)', marginBottom: 14 }}>
            {branding.logo_url
              ? <img src={branding.logo_url} alt="" style={{ height: 34, width: 'auto', maxWidth: 40, objectFit: 'contain', flexShrink: 0 }} />
              : <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--border)', display: 'grid', placeItems: 'center', fontSize: 19, flexShrink: 0 }}>{branding.flag || '🇲🇻'}</div>}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 19, fontWeight: 600, color: 'var(--text-strong)', lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {branding.title || 'Campaign CRM'}
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 8.5, letterSpacing: 0.5, color: 'var(--text-4)', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {branding.subtitle || 'Campaign CRM'}
              </div>
            </div>
            {user?.role === 'super_admin' && onEditBranding && (
              <button onClick={onEditBranding} title="Edit branding"
                style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', padding: 0, lineHeight: 0, display: 'inline-flex', flexShrink: 0 }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--link)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}>
                <Icon name="settings" size={14} />
              </button>
            )}
          </div>

          {/* Nav */}
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, overflowY: 'auto' }}>
            {navItems.map(item => {
              const isActive = currentPage === item.key;
              return (
                <button key={item.key}
                  onClick={() => navigate(item.path, { state: { ts: Date.now() } })}
                  aria-current={isActive ? 'page' : undefined}
                  style={{
                    position: 'relative', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px', borderRadius: 9, width: '100%', textAlign: 'left',
                    border: 'none', cursor: 'pointer',
                    background: isActive ? 'rgba(59,130,246,0.13)' : 'transparent',
                    color: isActive ? '#60a5fa' : 'var(--text-2)',
                    fontSize: 13, fontWeight: isActive ? 600 : 500, fontFamily: "'DM Mono', monospace",
                    transition: 'background 0.15s, color 0.15s',
                  }}
                  onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'var(--hover)'; e.currentTarget.style.color = 'var(--text)'; } }}
                  onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; } }}>
                  {isActive && <span style={{ position: 'absolute', left: -14, top: 8, bottom: 8, width: 3, borderRadius: '0 3px 3px 0', background: '#3b82f6' }} />}
                  <Icon name={item.icon} size={17} strokeWidth={isActive ? 2.4 : 2} />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Footer: user + theme + sign out */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '0 4px 10px' }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#3b82f61f', color: '#60a5fa', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <Icon name="user" size={15} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || 'User'}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{user?.role || 'viewer'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <ThemeToggle style={{ flex: '0 0 auto' }} />
              <button onClick={onSignOut}
                style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.4)',
                  background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: 11.5, fontWeight: 600,
                  fontFamily: "'DM Mono', monospace", display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  transition: 'background 0.15s, border-color 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.6)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'; }}>
                <Icon name="logout" size={14} color="#ef4444" /> Sign Out
              </button>
            </div>
          </div>
        </aside>

        {/* ── MAIN ── */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Top utility bar */}
          <div style={{
            position: 'sticky', top: 0, zIndex: 50, background: 'color-mix(in srgb, var(--surface) 88%, transparent)',
            backdropFilter: 'saturate(160%) blur(10px)', WebkitBackdropFilter: 'saturate(160%) blur(10px)',
            borderBottom: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)',
            padding: '11px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: 'var(--text-3)' }}>
              {activeNav && <Icon name={activeNav.icon} size={15} />}
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11.5, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-2)' }}>
                {PAGE_TITLES[currentPage] || 'Dashboard'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {canExp && <>
                {tool('download', 'Export CSV', onExport)}
                {tool('upload', 'Import Voters', () => importInputRef.current?.click())}
                {onExportTree && tool('database', 'Full Backup', onExportTree)}
                {onExportTree && tool('restore', 'Restore Backup', () => importTreeInputRef.current?.click())}
              </>}
              {hasEffectivePermission(user, 'canManageUsers') && tool('users', 'Team & Users', () => navigate('/team'))}
            </div>
          </div>

          {/* Portal host for VoterProfile's breadcrumb + Edit/Save bar
              (matches the mobile [data-main-header]; without this the bar,
              and its Edit button, vanished on the desktop sidebar shell). */}
          <div data-main-header />

          {/* Home accent bar */}
          {currentPage === 'home' && (
            <div style={{ height: 3, background: 'linear-gradient(to right, #22c55e 0%, #3b82f6 56%, #f59e0b 100%)' }} />
          )}

          {/* Content */}
          <div style={{ maxWidth: 1320, width: '100%', margin: '0 auto', padding: '26px 28px 40px', flex: 1 }}>
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* HEADER - PROJECT 1 STYLE */}
      <div data-main-header style={{
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        {/* Title Row */}
        <div style={{
          padding: isMobile ? '10px 12px' : '12px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div>
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: isMobile ? '18px' : '22px',
              color: 'var(--text-strong)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '2px'
            }}>
              {branding.logo_url
                ? <img src={branding.logo_url} alt="" style={{ height: 24, width: 'auto', objectFit: 'contain', verticalAlign: 'middle' }} />
                : <span>{branding.flag}</span>}{' '}{branding.title}
              {user?.role === 'super_admin' && onEditBranding && (
                <button onClick={onEditBranding} title="Edit branding"
                  style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', padding: '0 0 0 8px', lineHeight: 0, display: 'inline-flex' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--link)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}><Icon name="settings" size={15} /></button>
              )}
            </div>
            {!isMobile && (
              <div style={{
                fontSize: '10px',
                color: 'var(--text-3)',
                fontFamily: "'DM Mono', monospace",
                letterSpacing: '0.5px'
              }}>
                {branding.subtitle}
              </div>
            )}
          </div>

          {/* User Actions — on mobile, horizontally scrollable like the main nav so all buttons stay on one row */}
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            overflowX: isMobile ? 'auto' : 'visible',
            WebkitOverflowScrolling: 'touch',
            maxWidth: isMobile ? '100%' : 'none',
          }}>
            {!isMobile && (
              <div style={{
                fontSize: '11px',
                color: 'var(--text-2)',
                fontFamily: "'DM Mono', monospace",
                padding: '4px 0'
              }}>
                {user?.name || 'User'} · {user?.role || 'viewer'}
              </div>
            )}


            {hasEffectivePermission(user, 'canExport') && (
              <>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f && onImportFile) onImportFile(f); e.target.value = ''; }}
                />
                <button onClick={onExport} style={{
                  padding: '6px 12px',
                  background: '#1e40af',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: 600,
                  fontFamily: "'DM Mono', monospace",
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  transition: 'filter 0.15s'
                }}
                  onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.15)'}
                  onMouseLeave={e => e.currentTarget.style.filter = 'none'}>
                  <Icon name="download" size={14} color="#fff" /> CSV
                </button>

                <button onClick={() => importInputRef.current?.click()} style={{
                  padding: '6px 12px',
                  background: '#15803d',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: 600,
                  fontFamily: "'DM Mono', monospace",
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  transition: 'filter 0.15s'
                }}
                  onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.15)'}
                  onMouseLeave={e => e.currentTarget.style.filter = 'none'}>
                  <Icon name="upload" size={14} color="#fff" /> Import
                </button>

                {onExportTree && (
                  <>
                    <input
                      ref={importTreeInputRef}
                      type="file"
                      accept=".json"
                      style={{ display: 'none' }}
                      onChange={e => { const f = e.target.files?.[0]; if (f && onImportTreeFile) onImportTreeFile(f); e.target.value = ''; }}
                    />
                    <button onClick={onExportTree} title="Full backup (voters + family tree as one JSON file)" style={{
                      padding: '6px 12px',
                      background: '#7c3aed',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 600,
                      fontFamily: "'DM Mono', monospace",
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                      transition: 'filter 0.15s'
                    }}
                      onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.15)'}
                      onMouseLeave={e => e.currentTarget.style.filter = 'none'}>
                      <Icon name="database" size={14} color="#fff" /> Backup
                    </button>
                    <button onClick={() => importTreeInputRef.current?.click()} title="Restore from full backup JSON" style={{
                      padding: '6px 12px',
                      background: '#a855f7',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 600,
                      fontFamily: "'DM Mono', monospace",
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                      transition: 'filter 0.15s'
                    }}
                      onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.15)'}
                      onMouseLeave={e => e.currentTarget.style.filter = 'none'}>
                      <Icon name="restore" size={14} color="#fff" /> Restore
                    </button>
                  </>
                )}
              </>
            )}

            {!isMobile && hasEffectivePermission(user, 'canManageUsers') && (
              <button
                onClick={() => navigate('/team')}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--link)',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: 600,
                  fontFamily: "'DM Mono', monospace",
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  transition: 'border-color 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <Icon name="users" size={14} /> Users
              </button>
            )}

            <ThemeToggle />

            <button
              onClick={onSignOut}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: '1px solid rgba(239,68,68,0.4)',
                borderRadius: '8px',
                color: '#ef4444',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 600,
                fontFamily: "'DM Mono', monospace",
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                transition: 'background 0.15s, border-color 0.15s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.6)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'; }}
            >
              <Icon name="logout" size={14} color="#ef4444" /> Sign Out
            </button>
          </div>
        </div>

        {/* YELLOW SUPPORT BAR - Only on home */}
        {currentPage === 'home' && (
          <div style={{
            height: '8px',
            background: 'linear-gradient(to right, #22c55e 0%, #3b82f6 56%, #f59e0b 100%)',
            borderTop: '1px solid rgba(255,255,255,0.05)'
          }} />
        )}

        {/* NAVIGATION BUTTONS - Below yellow bar.
            On mobile, swipe horizontally to reach tabs beyond the viewport
            (flex-nowrap + overflow-x auto). On desktop, tabs wrap as before. */}
        <div className="scrollbar-hide" style={{
          padding: isMobile ? '10px 12px' : '12px 20px',
          display: 'flex',
          gap: '8px',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          overflowX: isMobile ? 'auto' : 'visible',
          WebkitOverflowScrolling: 'touch',
          borderBottom: '1px solid var(--border)',
        }}>
          {navItems.map(item => {
            const isActive = currentPage === item.key;
            return (
              <button
                key={item.key}
                onClick={() => navigate(item.path, { state: { ts: Date.now() } })}
                aria-current={isActive ? 'page' : undefined}
                style={{
                  padding: '8px 13px',
                  background: isActive ? 'rgba(59,130,246,0.13)' : 'transparent',
                  border: isActive ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
                  borderRadius: '8px',
                  color: isActive ? '#60a5fa' : 'var(--text-2)',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: isActive ? 600 : 500,
                  fontFamily: "'DM Mono', monospace",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                  flexShrink: 0,  // prevents tabs from squishing in horizontal-scroll mode
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={e => { if (isActive) return; e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; }}
                onMouseLeave={e => { if (isActive) return; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}
              >
                <Icon name={item.icon} size={15} strokeWidth={isActive ? 2.4 : 2} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

      </div>

      {/* MAIN CONTENT */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: 20 }}>{children}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// LOGIN PAGE
// ══════════════════════════════════════════════════════════

function BrandingModal({ branding, onClose, onSave }) {
  const [form, setForm] = useState(branding);
  const [uploading, setUploading] = useState(false);
  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--surface-2)',
    color: 'var(--text)', fontSize: '14px', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', fontSize: 11, color: 'var(--text-2)', marginBottom: 6, fontFamily: "'DM Mono', monospace" };

  // Resize and convert image to base64 (kept under ~100KB)
  const handleLogoUpload = (file) => {
    if (!file) return;
    setUploading(true);
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    img.onload = () => {
      const maxDim = 200;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) { height = Math.round(height * maxDim / width); width = maxDim; }
        else                 { width = Math.round(width * maxDim / height); height = maxDim; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/png');
      setForm(f => ({ ...f, logo_url: dataUrl }));
      setUploading(false);
    };
    img.onerror = () => { setUploading(false); alert('Could not read image'); };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, width: '100%', maxWidth: 520, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: 'var(--text-strong)', marginBottom: 6 }}>
          <Icon name="settings" size={18} /> Branding Settings
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 20 }}>
          These appear on the login screen, header, and dashboard.
        </div>

        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label style={labelStyle}>LOGO (optional — overrides flag/emoji)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" style={{ width: 48, height: 48, objectFit: 'contain', background: 'var(--surface-2)', borderRadius: 6, border: '1px solid var(--border)', padding: 4 }} />
              ) : (
                <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)', borderRadius: 6, border: '1px dashed var(--border)', fontSize: 22 }}>
                  {form.flag || '🇲🇻'}
                </div>
              )}
              <label style={{ display: 'inline-block', padding: '8px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--link)', cursor: 'pointer', fontSize: 12 }}>
                {uploading ? 'Processing…' : (form.logo_url ? 'Replace' : 'Upload Logo')}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleLogoUpload(e.target.files?.[0])} />
              </label>
              {form.logo_url && (
                <button onClick={() => setForm(f => ({ ...f, logo_url: '' }))}
                  style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>
                  Remove
                </button>
              )}
            </div>
          </div>
          <div>
            <label style={labelStyle}>FLAG / EMOJI (used if no logo)</label>
            <input value={form.flag || ''} onChange={e => setForm({ ...form, flag: e.target.value })} style={inputStyle} placeholder="🇲🇻" />
          </div>
          <div>
            <label style={labelStyle}>TITLE</label>
            <input value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} style={inputStyle} placeholder="e.g., Fonadhoo Constituency" />
          </div>
          <div>
            <label style={labelStyle}>HEADER SUBTITLE</label>
            <input value={form.subtitle || ''} onChange={e => setForm({ ...form, subtitle: e.target.value })} style={inputStyle} placeholder="e.g., FONADHOO · MAAMENDHOO · 21ST PARLIAMENT 2028" />
          </div>
          <div>
            <label style={labelStyle}>LOGIN PAGE SUBTITLE</label>
            <input value={form.login_subtitle || ''} onChange={e => setForm({ ...form, login_subtitle: e.target.value })} style={inputStyle} placeholder="e.g., CAMPAIGN CRM · 21ST PARLIAMENT 2028" />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '10px 18px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button onClick={() => onSave(form)}
            style={{ padding: '10px 18px', borderRadius: 6, border: 'none', background: '#10b981', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function LoginPage({ onLogin, branding = {} }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { data, error: loginError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (loginError) {
      setError(loginError.message);
      setLoading(false);
      return;
    }

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', data.user.id)
      .single();

    if (profile && profile.is_active) {
      onLogin({ user: data.user, profile });
    } else if (profile && !profile.is_active) {
      setError('Account inactive. Contact admin.');
      await supabase.auth.signOut();
    } else if (profileError && profileError.code !== 'PGRST116') {
      // PGRST116 = no row (genuinely no profile yet) — any other error is a
      // real backend problem and shouldn't be blamed on a missing profile.
      console.error('[login] profile lookup failed:', profileError);
      setError('Something went wrong loading your profile. Please try again.');
      await supabase.auth.signOut();
    } else {
      setError('No profile found. Ask your admin to add you via the Team page.');
      await supabase.auth.signOut();
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg)',
      padding: '20px',
      position: 'relative'
    }}>
      <ThemeToggle style={{ position: 'absolute', top: 16, right: 16 }} />
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: '18px',
        padding: '40px 36px',
        maxWidth: '420px',
        width: '100%',
        boxShadow: 'var(--shadow-lg)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Top accent */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #22c55e, #3b82f6 56%, #f59e0b)' }} />

        <div style={{
          textAlign: 'center',
          marginBottom: '14px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          {branding.logo_url
            ? <img src={branding.logo_url} alt="" style={{ height: 60, width: 'auto', objectFit: 'contain' }} />
            : <div style={{ width: 60, height: 60, borderRadius: 16, background: 'var(--surface-2)', border: '1px solid var(--border)', display: 'grid', placeItems: 'center', fontSize: 30 }}>{branding.flag || '🇲🇻'}</div>}
        </div>

        <h1 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: '30px',
          fontWeight: 600,
          color: 'var(--text-strong)',
          marginBottom: '6px',
          textAlign: 'center',
          letterSpacing: '-0.3px'
        }}>
          {branding.title || 'Campaign CRM'}
        </h1>

        <p style={{
          color: 'var(--text-3)',
          marginBottom: '30px',
          fontSize: '11px',
          textAlign: 'center',
          fontFamily: "'DM Mono', monospace",
          letterSpacing: '1px'
        }}>
          {branding.login_subtitle || 'CAMPAIGN CRM'}
        </p>

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="login-email" style={{
              display: 'block',
              color: 'var(--text-2)',
              marginBottom: '7px',
              fontSize: '11px',
              fontWeight: 500,
              fontFamily: "'DM Mono', monospace",
              letterSpacing: '0.5px'
            }}>
              EMAIL
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-4)', lineHeight: 0, pointerEvents: 'none' }}>
                <Icon name="mail" size={16} />
              </span>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 38px',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  color: 'var(--text-strong)',
                  fontSize: '14px',
                  fontFamily: "'DM Mono', monospace"
                }}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="login-password" style={{
              display: 'block',
              color: 'var(--text-2)',
              marginBottom: '7px',
              fontSize: '11px',
              fontWeight: 500,
              fontFamily: "'DM Mono', monospace",
              letterSpacing: '0.5px'
            }}>
              PASSWORD
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-4)', lineHeight: 0, pointerEvents: 'none' }}>
                <Icon name="lock" size={16} />
              </span>
              <input
                id="login-password"
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  width: '100%',
                  padding: '12px 42px 12px 38px',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  color: 'var(--text-strong)',
                  fontSize: '14px',
                  fontFamily: "'DM Mono', monospace"
                }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPw(s => !s)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', padding: 6, lineHeight: 0, display: 'inline-flex' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--text-2)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}
              >
                <Icon name={showPw ? 'eyeOff' : 'eye'} size={16} />
              </button>
            </div>
          </div>

          {error && (
            <div role="alert" style={{
              background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.35)',
              color: '#ef4444',
              padding: '10px 12px',
              borderRadius: '10px',
              fontSize: '12px',
              marginBottom: '16px',
              fontFamily: "'DM Mono', monospace"
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '13px',
              background: loading ? 'var(--surface-3)' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: "'DM Mono', monospace",
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: loading ? 'none' : '0 6px 18px rgba(37,99,235,0.35)',
              transition: 'filter 0.15s'
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = 'brightness(1.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
          >
            {loading && <span style={{ width: 15, height: 15, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={{
          color: 'var(--text-3)',
          fontSize: '11px',
          textAlign: 'center',
          marginTop: '22px',
          fontFamily: "'DM Mono', monospace"
        }}>
          Contact administrator for access
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// MAIN APP WITH ROUTING
// ══════════════════════════════════════════════════════════

export default function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allVoters, setAllVoters] = useState([]);
  const [votersLoading, setVotersLoading] = useState(true);
  const [families, setFamilies] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [opponentNames, setOpponentNames] = useState([]);
  const [patchAgents, setPatchAgents] = useState([]);
  const [branding, setBranding] = useState(() => {
    const defaults = {
      flag: '🇲🇻',
      title: 'Fonadhoo Constituency',
      subtitle: 'FONADHOO · MAAMENDHOO · 21ST PARLIAMENT 2028',
      login_subtitle: 'CAMPAIGN CRM · 21ST PARLIAMENT 2028',
    };
    try {
      const cached = localStorage.getItem('branding');
      return cached ? { ...defaults, ...JSON.parse(cached) } : defaults;
    } catch {
      return defaults;
    }
  });
  const [showBrandingModal, setShowBrandingModal] = useState(false);

  // Load locations once
  useEffect(() => {
    if (session) {
      supabase.from('locations').select('*').order('atoll').order('island')
        .then(({ data, error }) => {
          if (error) console.error('[locations] load failed:', error);
          setAllLocations(data || []);
        });
    }
  }, [session]);

  // Load branding settings (every authenticated user)
  useEffect(() => {
    if (!session) return;
    supabase.from('app_settings').select('value').eq('key', 'branding').single()
      .then(({ data, error }) => {
        // PGRST116 = no row yet (fresh install) — expected, not an error.
        if (error && error.code !== 'PGRST116') console.error('[app_settings] branding load failed:', error);
        if (data?.value) {
          setBranding(prev => ({ ...prev, ...data.value }));
          try { localStorage.setItem('branding', JSON.stringify(data.value)); } catch {}
        }
      });
  }, [session]);

  // Load patch agents — use same query pattern as TeamDashboard (which works)
  // and filter client-side. Fall back to supabaseAdmin if RLS blocks rows.
  useEffect(() => {
    if (!session) return;
    const tryLoad = async () => {
      // Use regular supabase — RLS lets seniors (super_admin etc.) read all profiles
      const { data: rows, error } = await supabase
        .from('profiles').select('id, full_name, role').eq('is_active', true).order('full_name');
      if (error) console.error('[profiles] patch agents load failed:', error);
      setPatchAgents((rows || []).filter(r => r.role === 'patch_agent'));
    };
    tryLoad();
  }, [session]);

  // Load opponents once for all roles — app_settings is readable by everyone.
  // Falls back to localStorage so admin users see the dropdown instantly even
  // before app_settings is populated.
  useEffect(() => {
    if (!session) return;
    supabase.from('app_settings').select('value').eq('key', 'opponents').single()
      .then(({ data, error }) => {
        // PGRST116 = no row yet (fresh install) — expected, not an error.
        if (error && error.code !== 'PGRST116') console.error('[app_settings] opponents load failed:', error);
        if (Array.isArray(data?.value) && data.value.length > 0) {
          setOpponentNames(data.value.map(o => typeof o === 'string' ? o : o.name).filter(Boolean));
        } else {
          try {
            const local = JSON.parse(localStorage.getItem('crm_opponents') || '[]')
              .map(o => typeof o === 'string' ? o : o.name).filter(Boolean);
            if (local.length > 0) setOpponentNames(local);
          } catch {}
        }
      });
  }, [session]);

  // Load all voters once
  useEffect(() => {
    if (session) {
      loadAllVoters();
      loadFamilies();
    }
  }, [session]);

  // Families + family_members are loaded once and shared. VoterProfile reads
  // this prop instead of refetching on every navigation, which was the main
  // cause of slow family-tree clicks.
  const loadFamilies = async () => {
    const { data } = await supabase.from('families').select('*, family_members(*)');
    if (!data) return;
    const normalized = data.map(f => ({
      ...f,
      members: (f.family_members || []).map(m => ({
        ...m,
        parent_ids: m.parent_ids || [],
        spouse_id: m.spouse_id || null,
      })),
    }));
    setFamilies(normalized);
  };


  const loadAllVoters = async () => {
    setVotersLoading(true);
    const PAGE = 1000;

    // Get total count first so we can fetch all pages in parallel.
    const { count, error: countError } = await supabase
      .from('voters_secured')
      .select('*', { count: 'exact', head: true });
    if (countError) {
      console.error('loadAllVoters count error:', countError);
      setVotersLoading(false);
      return;
    }

    const pages = Math.ceil((count || 0) / PAGE);
    const fetches = Array.from({ length: pages }, (_, i) =>
      supabase
        .from('voters_secured')
        .select('*')
        .order('name')
        .range(i * PAGE, i * PAGE + PAGE - 1)
    );

    const results = await Promise.all(fetches);
    const all = [];
    for (const { data, error } of results) {
      if (error) { console.error('loadAllVoters error:', error); continue; }
      if (data) all.push(...data);
    }
    all.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    setAllVoters(all);
    setVotersLoading(false);
  };

  const EXPORT_COLS = [
    'id_number','name','dob','age','gender',
    'phone','phone2','phone3',
    'atoll','island','ward','house','house_name','house_number','neighborhood',
    'current_location','ballot_box','ballot_box_r1','ballot_box_r2',
    'household_id','family_id','influencer',
    'support','support_strength','preferred_candidate',
    'needs_transport','transport_type','voted_2023','voted_2018','voted_2028',
    'notes','gotv_assigned_to',
  ];

  const handleExport = () => {
    const esc = v => {
      if (v == null) return '';
      const s = Array.isArray(v) ? v.join(';') : String(v);
      return (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      EXPORT_COLS.join(','),
      ...allVoters.map(v => EXPORT_COLS.map(c => esc(v[c])).join(',')),
    ].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `voters_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleImportFile = async (file) => {
    const BOOL_COLS = new Set(['first_time_voter','relocated','party_active','party_family_same',
      'filled_form','fingerprinted_by_you','joined_mdp','needs_transport','needs_reminder',
      'needs_assistance','voted_2023','voted_2018','voted_2028','influencer']);
    const INT_COLS = new Set(['age','persuadability_score','party_event_attendance','contact_count']);

    // Normalize a header: lowercase, strip whitespace/underscores/hyphens.
    // "ID Number", "id_number", "ID-NUMBER" all become "idnumber".
    const normalizeHeader = (s) => (s || '').trim().toLowerCase().replace(/[\s_\-]+/g, '');

    // Map of normalized header variations → canonical DB column name.
    // Add more aliases over time as users send weird CSVs.
    const HEADER_ALIASES = (() => {
      const m = {};
      const add = (canonical, ...aliases) => {
        m[normalizeHeader(canonical)] = canonical;
        for (const a of aliases) m[normalizeHeader(a)] = canonical;
      };
      add('name', 'full name', 'fullname', 'voter name', 'voter', 'person name');
      add('id_number', 'id', 'idnumber', 'nid', 'national id', 'nationalid',
        'national identification number', 'national identity number', 'voter id', 'voterid');
      add('dob', 'date of birth', 'dateofbirth', 'birthdate', 'birthday', 'born', 'birth date');
      add('age');
      add('gender', 'sex');
      add('phone', 'mobile', 'tel', 'phone number', 'mobile number', 'primary phone', 'contact', 'cell');
      add('phone2', 'secondary phone', 'alt phone', 'alternative phone', 'phone 2', 'second phone');
      add('phone3', 'third phone', 'phone 3');
      add('atoll');
      add('island');
      add('ward', 'constituency ward');
      add('house', 'address', 'home address');
      add('house_name', 'housename');
      add('house_number', 'housenumber', 'door number', 'house no');
      add('neighborhood', 'neighbourhood', 'locality', 'area');
      add('current_location', 'currentlocation', 'location', 'living in');
      add('ballot_box', 'ballotbox', 'box');
      add('ballot_box_r1', 'ballot box round 1', 'box round 1', 'ballotboxr1');
      add('ballot_box_r2', 'ballot box round 2', 'box round 2', 'ballotboxr2');
      add('household_id', 'household', 'householdid');
      add('family_id', 'family', 'familyid');
      add('influencer', 'is influencer', 'key influencer', 'keyinfluencer');
      add('support', 'support level', 'supportlevel');
      add('support_strength', 'supportstrength');
      add('preferred_candidate', 'preferredcandidate', 'candidate', 'supports candidate');
      add('needs_transport', 'needstransport', 'transport needed');
      add('transport_type', 'transporttype', 'transport method');
      add('voted_2023', 'voted2023');
      add('voted_2018', 'voted2018');
      add('voted_2028', 'voted2028', 'voted');
      add('notes', 'comment', 'comments', 'remarks');
      add('gotv_assigned_to', 'gotv volunteer', 'volunteer', 'assigned to', 'gotvassignedto');
      return m;
    })();

    const parseCSVLine = (line) => {
      const result = []; let cur = ''; let inQ = false;
      for (let i = 0; i < line.length; i++) {
        if (line[i] === '"') {
          if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
          else inQ = !inQ;
        } else if (line[i] === ',' && !inQ) { result.push(cur); cur = ''; }
        else cur += line[i];
      }
      result.push(cur);
      return result;
    };

    const coerce = (col, raw) => {
      if (raw === '' || raw == null) return null;
      if (BOOL_COLS.has(col)) return raw === 'true' || raw === '1' || raw === 'TRUE';
      if (INT_COLS.has(col)) { const n = parseInt(raw, 10); return isNaN(n) ? null : n; }
      return raw;
    };

    try {
      const text = await file.text();
      const lines = text.replace(/\r/g, '').split('\n').filter(Boolean);
      if (lines.length < 2) { alert('CSV is empty or has no data rows.'); return; }
      const rawHeaders = parseCSVLine(lines[0]).map(h => h.trim().replace(/^﻿/, ''));
      // Resolve each raw header to a canonical column name via aliases.
      // Unrecognized headers map to null and are silently ignored.
      const resolved = rawHeaders.map(h => HEADER_ALIASES[normalizeHeader(h)] || null);
      const recognized = resolved.filter(Boolean);
      if (!recognized.includes('name')) {
        alert('CSV must have a "name" column (or a recognized alias like "Full Name", "Voter Name").');
        return;
      }
      const rows = lines.slice(1).map(line => {
        const vals = parseCSVLine(line);
        const obj = {};
        resolved.forEach((col, i) => {
          if (!col) return; // skip unrecognized columns
          obj[col] = coerce(col, vals[i]?.trim() ?? null);
        });
        return obj;
      }).filter(r => r.name);

      const BATCH = 200;
      let imported = 0;
      for (let i = 0; i < rows.length; i += BATCH) {
        const batch = rows.slice(i, i + BATCH);
        const { error } = await supabase.from('voters').upsert(batch, { onConflict: 'id_number', ignoreDuplicates: false });
        if (error) { alert(`Import error at row ${i + 1}: ${error.message}`); return; }
        imported += batch.length;
      }
      const unknownHeaders = rawHeaders.filter((_, i) => !resolved[i]);
      const summary = `✅ ${imported} voters imported successfully.` +
        (unknownHeaders.length ? `\n\nIgnored unrecognized columns: ${unknownHeaders.join(', ')}` : '');
      alert(summary);
      loadAllVoters();
    } catch (err) {
      alert('Failed to read file: ' + err.message);
    }
  };

  // ── Full Backup (single JSON) ────────────────────────────
  // One file containing voters + families + family_members. Voter links inside
  // family_members are exported using the voter's id_number (a stable text key)
  // so they survive a full re-import where serial ids change.
  const handleExportTree = async () => {
    // Paginate voters past Supabase's 1000-row default page size.
    const fetchAll = async (table) => {
      const PAGE = 1000;
      const all = [];
      for (let i = 0; i < 200; i++) { // cap at 200k rows
        const { data, error } = await supabase.from(table).select('*').range(i * PAGE, i * PAGE + PAGE - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        all.push(...data);
        if (data.length < PAGE) break;
      }
      return all;
    };
    const [voters, families, members] = await Promise.all([
      fetchAll('voters'),
      fetchAll('families'),
      fetchAll('family_members'),
    ]);
    const voterIdToNumber = new Map();
    for (const v of voters) if (v.id != null && v.id_number) voterIdToNumber.set(v.id, v.id_number);
    const membersWithBridge = members.map(m => ({
      ...m,
      _voter_id_number: m.voter_id ? (voterIdToNumber.get(m.voter_id) || null) : null,
    }));
    const backup = {
      version: 1,
      exported_at: new Date().toISOString(),
      voters,
      families,
      family_members: membersWithBridge,
    };
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' }));
    a.download = `campaign_backup_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleImportTreeFile = async (file) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data || typeof data !== 'object') { alert('Invalid backup file.'); return; }
      const { voters: bVoters = [], families: bFamilies = [], family_members: bMembers = [] } = data;
      if (!Array.isArray(bVoters) || !Array.isArray(bFamilies) || !Array.isArray(bMembers)) {
        alert('Backup file is missing one of: voters, families, family_members.');
        return;
      }
      if (!window.confirm(
        `Restore backup?\n\n` +
        `• ${bVoters.length} voters\n` +
        `• ${bFamilies.length} families\n` +
        `• ${bMembers.length} family members\n\n` +
        `Existing records with matching id_number (voters) or id (families / members) will be updated.`
      )) return;

      // Step 1: voters — strip serial id so DB assigns fresh, conflict on id_number
      const voterRows = bVoters
        .filter(v => v?.name)
        .map(v => { const { id, ...rest } = v; return rest; });
      const BATCH = 200;
      for (let i = 0; i < voterRows.length; i += BATCH) {
        const batch = voterRows.slice(i, i + BATCH);
        const { error } = await supabase.from('voters').upsert(batch, { onConflict: 'id_number', ignoreDuplicates: false });
        if (error) { alert(`Voter restore failed at row ${i + 1}: ${error.message}`); return; }
      }

      // Step 2: build id_number → new voter.id map from current voters (paginated)
      const idNumberToVoter = new Map();
      const PAGE = 1000;
      for (let p = 0; p < 200; p++) {
        const { data: freshVoters } = await supabase.from('voters').select('id, id_number').range(p * PAGE, p * PAGE + PAGE - 1);
        if (!freshVoters || freshVoters.length === 0) break;
        for (const v of freshVoters) if (v.id_number) idNumberToVoter.set(v.id_number, v.id);
        if (freshVoters.length < PAGE) break;
      }

      // Step 3: families — strip created_by (FK to profiles.id from the source
      // project, won't exist in a fresh destination). Same for any other
      // profile-FK columns that might creep in over time.
      if (bFamilies.length) {
        const famRows = bFamilies.map(f => {
          const { created_by, ...rest } = f;
          return rest;
        });
        const { error: famErr } = await supabase.from('families').upsert(famRows, { onConflict: 'id' });
        if (famErr) { alert('Family restore failed: ' + famErr.message); return; }
      }

      // Step 4: family_members — translate voter_id via id_number bridge
      const memberRows = bMembers.map(m => {
        const { _voter_id_number, ...rest } = m;
        const newVoterId = _voter_id_number ? (idNumberToVoter.get(_voter_id_number) || null) : null;
        return { ...rest, voter_id: newVoterId };
      });
      let memberImported = 0;
      for (let i = 0; i < memberRows.length; i += BATCH) {
        const batch = memberRows.slice(i, i + BATCH);
        const { error } = await supabase.from('family_members').upsert(batch, { onConflict: 'id' });
        if (error) { alert(`Family member restore failed at row ${i + 1}: ${error.message}`); return; }
        memberImported += batch.length;
      }

      alert(`✅ Backup restored.\n• ${voterRows.length} voters\n• ${bFamilies.length} families\n• ${memberImported} family members`);
      loadAllVoters();
    } catch (err) {
      alert('Failed to read backup file: ' + err.message);
    }
  };

  useEffect(() => {
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        handleSessionChange(session);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        handleSessionChange(session);
      } else {
        setSession(null);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSessionChange = async (session) => {
    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .single();

    if (profileData && profileData.is_active) {
      setSession(session);
      setProfile(profileData);

      // Backfill opponents into app_settings so all roles (including patch agents)
      // can read the opponent list without needing storage access.
      if (['super_admin', 'campaign_manager', 'candidate'].includes(profileData.role)) {
        supabase.from('app_settings').select('value').eq('key', 'opponents').single()
          .then(({ data: existing }) => {
            if (existing?.value && Array.isArray(existing.value) && existing.value.length > 0) return;
            // Try localStorage first (fastest — admin always has this after saving opponents)
            let parsed = [];
            try {
              parsed = JSON.parse(localStorage.getItem('crm_opponents') || '[]')
                .map(o => typeof o === 'string' ? { name: o } : o)
                .filter(o => o.name);
            } catch {}
            if (parsed.length > 0) {
              supabase.from('app_settings').upsert({ key: 'opponents', value: parsed });
              return;
            }
            // Fall back to storage
            supabase.storage.from('photos').download('config/opponents.json')
              .then(({ data: blob }) => {
                if (!blob) return;
                blob.text().then(text => {
                  try {
                    const list = JSON.parse(text)
                      .map(o => typeof o === 'string' ? { name: o } : o)
                      .filter(o => o.name);
                    if (list.length > 0) supabase.from('app_settings').upsert({ key: 'opponents', value: list });
                  } catch {}
                });
              });
          });
      }
    }
    setLoading(false);
  };

  const handleLogin = ({ user, profile }) => {
    setSession({ user });
    setProfile(profile);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
  };

  // Anyone without canViewAll is scoped to their assigned_territory.
  // HouseholdView intentionally still gets allVoters so patch_agent can
  // search any voter when building a family group.
  // NOTE: useMemo MUST be called before any early returns below — React rules of hooks.
  const scopedVoters = useMemo(() => {
    if (hasEffectivePermission(profile, 'canViewAll')) return allVoters;
    const terrs = Array.isArray(profile?.assigned_territories) ? profile.assigned_territories : [];
    return allVoters.filter(v =>
      (profile?.assigned_territory && (v.island === profile.assigned_territory || v.ward === profile.assigned_territory)) ||
      (terrs.length > 0 && (terrs.includes(v.island) || terrs.includes(v.ward))) ||
      (v.gotv_assigned_to === profile?.id)
    );
  }, [allVoters, profile?.id, profile?.role, profile?.assigned_territory, profile?.assigned_territories, profile?.temp_permissions, profile?.revoked_permissions]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        color: 'var(--link)',
        fontFamily: "'DM Mono', monospace"
      }}>
        Loading...
      </div>
    );
  }

  if (!session) {
    return <LoginPage onLogin={handleLogin} branding={branding} />;
  }

  return (
    <>
    <Routes>
      <Route path="/" element={
        hasEffectivePermission(profile, 'canLogOutreach') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="home">
            <HomePage supabase={supabase} user={profile} allVoters={scopedVoters} branding={branding} />
          </Project1Layout>
        ) : hasEffectivePermission(profile, 'canManageEvents')
          ? <Navigate to="/events" replace />
          : <Navigate to="/outreach" replace />
      } />

      <Route path="/voter/:voterId" element={
        hasEffectivePermission(profile, 'canLogOutreach')
          ? <VoterProfileWrapper supabase={supabase} user={profile} onSignOut={handleSignOut} allVoters={scopedVoters} allLocations={allLocations} opponentNames={opponentNames} families={families} refreshFamilies={loadFamilies} />
          : <Navigate to="/events" replace />
      } />

      <Route path="/gotv" element={
        hasEffectivePermission(profile, 'canViewVotes') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="gotv">
            <GOTVDashboard supabase={supabase} user={profile} allVoters={scopedVoters} allLocations={allLocations} patchAgents={patchAgents} />
          </Project1Layout>
        ) : <Navigate to="/" replace />
      } />

      <Route path="/search" element={
        hasEffectivePermission(profile, 'canLogOutreach') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="search">
            <AdvancedSearch supabase={supabase} user={profile} allVoters={scopedVoters} allLocations={allLocations} />
          </Project1Layout>
        ) : <Navigate to="/" replace />
      } />

      <Route path="/households" element={
        hasEffectivePermission(profile, 'canLogOutreach') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="households">
            <HouseholdView supabase={supabase} user={profile} allVoters={scopedVoters} />
          </Project1Layout>
        ) : <Navigate to="/" replace />
      } />

      <Route path="/outreach" element={
        hasEffectivePermission(profile, 'canLogOutreach') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="outreach">
            <EnhancedOutreach supabase={supabase} user={profile} allVoters={scopedVoters} />
          </Project1Layout>
        ) : <Navigate to="/" replace />
      } />

      <Route path="/reminders" element={
        hasEffectivePermission(profile, 'canLogOutreach') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="reminders">
            <RemindersPanel supabase={supabase} user={profile} allVoters={scopedVoters} />
          </Project1Layout>
        ) : <Navigate to="/" replace />
      } />

      <Route path="/team" element={
        hasEffectivePermission(profile, 'canManageUsers') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="team">
            <TeamDashboard supabase={supabase} user={profile} allVoters={allVoters} />
          </Project1Layout>
        ) : <Navigate to="/" replace />
      } />

      <Route path="/analytics" element={
        hasEffectivePermission(profile, 'canViewAnalytics') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="analytics">
            <Analytics supabase={supabase} user={profile} allVoters={scopedVoters} />
          </Project1Layout>
        ) : <Navigate to="/" replace />
      } />

      <Route path="/events" element={
        hasEffectivePermission(profile, 'canManageEvents') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="events">
            <EventManagement supabase={supabase} user={profile} allVoters={scopedVoters} allLocations={allLocations} />
          </Project1Layout>
        ) : <Navigate to="/" replace />
      } />

      <Route path="/trips" element={
        hasEffectivePermission(profile, 'canManageTrips') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="trips">
            <CampaignTrips supabase={supabase} user={profile} allLocations={allLocations} allVoters={allVoters} />
          </Project1Layout>
        ) : <Navigate to="/" replace />
      } />

      <Route path="/calendar" element={
        hasEffectivePermission(profile, 'canManageEvents') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="calendar">
            <CampaignCalendar supabase={supabase} allVoters={scopedVoters} allLocations={allLocations} />
          </Project1Layout>
        ) : <Navigate to="/" replace />
      } />

      <Route path="/count" element={
        hasEffectivePermission(profile, 'canViewVotes') ? (
          <Project1Layout user={profile} onSignOut={handleSignOut} onExport={handleExport} onImportFile={handleImportFile} onExportTree={handleExportTree} onImportTreeFile={handleImportTreeFile} branding={branding} onEditBranding={() => setShowBrandingModal(true)} currentPage="count">
            <BallotCount supabase={supabase} user={profile} allVoters={scopedVoters} />
          </Project1Layout>
        ) : <Navigate to="/" replace />
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>

    {showBrandingModal && (
      <BrandingModal
        branding={branding}
        onClose={() => setShowBrandingModal(false)}
        onSave={async (next) => {
          const { error } = await supabase.from('app_settings').upsert({ key: 'branding', value: next });
          if (error) { alert('Save failed: ' + error.message); return; }
          setBranding(next);
          try { localStorage.setItem('branding', JSON.stringify(next)); } catch {}
          setShowBrandingModal(false);
        }}
      />
    )}
    </>
  );
}

// ══════════════════════════════════════════════════════════
// VOTER PROFILE WRAPPER (handles URL param)
// ══════════════════════════════════════════════════════════

function VoterProfileWrapper({ supabase, user, onSignOut, allVoters, allLocations = [], opponentNames = [], families = [], refreshFamilies }) {
  const { voterId } = useParams();
  const navigate = useNavigate();
  const [voter, setVoter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top when profile opens
    loadVoter();
  }, [voterId]);

  const loadVoter = async () => {
    const { data } = await supabase
      .from('voters_secured')
      .select('*')
      .eq('id', voterId)
      .single();

    setVoter(data);
    setLoading(false);
  };

  if (loading) {
    return (
      <Project1Layout user={user} onSignOut={onSignOut} currentPage="home">
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-2)' }}>
          Loading voter...
        </div>
      </Project1Layout>
    );
  }

  if (!voter) {
    return (
      <Project1Layout user={user} onSignOut={onSignOut} currentPage="home">
        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>
          Voter not found
        </div>
      </Project1Layout>
    );
  }

  return (
    <Project1Layout user={user} onSignOut={onSignOut} currentPage="home">
      <VoterProfile
        voter={voter}
        onClose={() => navigate(-1)}
        supabase={supabase}
        profile={user}
        allLocations={allLocations}
        opponentNames={opponentNames}
        initialAllVoters={allVoters}
        initialFamilies={families}
        refreshFamilies={refreshFamilies}
      />
    </Project1Layout>
  );
}

// ══════════════════════════════════════════════════════════
// HOME PAGE (Voter List)
// ══════════════════════════════════════════════════════════


// ============================================================
// HOME PAGE - DASHBOARD STYLE (REPLACES VOTER LIST)
// ============================================================

const SC_HOME = {
  'Strong Support':  { bg:'var(--sup-strong-bg)',       text:'var(--sup-strong-text)',       dot:'var(--sup-strong-dot)' },
  'Leaning Support': { bg:'var(--sup-lean-bg)',         text:'var(--sup-lean-text)',         dot:'var(--sup-lean-dot)' },
  'Undecided':       { bg:'var(--sup-undecided-bg)',    text:'var(--sup-undecided-text)',    dot:'var(--sup-undecided-dot)' },
  'Leaning Against': { bg:'var(--sup-against-lean-bg)', text:'var(--sup-against-lean-text)', dot:'var(--sup-against-lean-dot)' },
  'Strong Against':  { bg:'var(--sup-against-bg)',      text:'var(--sup-against-text)',      dot:'var(--sup-against-dot)' },
};

const SUPPORT_LEVELS_HOME = [
  'Strong Support',
  'Leaning Support',
  'Undecided',
  'Leaning Against',
  'Strong Against'
];

function HomePage({ supabase, user, allVoters = [], branding = {} }) {
  const navigate = useNavigate();
  const canSeeVotes = hasEffectivePermission(user, 'canViewVotes');
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange); };
  }, []);
  const [drillDown, setDrillDown] = useState(null); // 'all' | 'supporters' | 'undecided'
  const drillDownRef = React.useRef(null);
  React.useEffect(() => {
    if (drillDown && drillDownRef.current) {
      drillDownRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [drillDown]);

  // Calculate statistics
  const total = allVoters.length;
  const supportCounts = {};
  SUPPORT_LEVELS_HOME.forEach(level => {
    supportCounts[level] = allVoters.filter(v => v.support === level).length;
  });
  
  const strongSupport = supportCounts['Strong Support'] || 0;
  const leaningSupport = supportCounts['Leaning Support'] || 0;
  const totalSupport = strongSupport + leaningSupport;
  const totalAgainst = (supportCounts['Strong Against'] || 0) + (supportCounts['Leaning Against'] || 0);
  const undecided = supportCounts['Undecided'] || 0;
  
  // Format a raw percentage accurately:
  //   >= 1 %    → integer    "52%"
  //   0.1–1 %   → 1 decimal  "0.5%"
  //   < 0.1 %   → 2 decimals "0.03%"
  // Guard: never display 0% for a non-zero value, or 100% for a non-100 value.
  // Format a raw percentage accurately. Never rounds to 0% or 100%
  // unless the value is exactly 0 or 100.
  const fmtPct = (raw) => {
    if (raw === 0)   return '0%';
    if (raw === 100) return '100%';
    // Try progressively more decimal places until the result isn't misleading.
    for (const dp of [0, 1, 2, 3]) {
      const s = raw.toFixed(dp);
      const n = parseFloat(s);
      if (n > 0 && n < 100) return `${s}%`;
    }
    // Fallback — shouldn't reach here for real-world percentages.
    return `${raw.toFixed(2)}%`;
  };

  const supportPct = total > 0 ? (totalSupport / total * 100) : 0;
  const needed = Math.ceil(total / 2);
  const toWin = needed - totalSupport;

  const supportSegments = [
    { label: 'Strong Support',  value: supportCounts['Strong Support']  || 0, color: 'var(--sup-strong-dot)' },
    { label: 'Leaning Support', value: supportCounts['Leaning Support'] || 0, color: 'var(--sup-lean-dot)' },
    { label: 'Undecided',       value: undecided,                            color: 'var(--text-3)' },
    { label: 'Leaning Against', value: supportCounts['Leaning Against'] || 0, color: 'var(--sup-against-lean-dot)' },
    { label: 'Strong Against',  value: supportCounts['Strong Against']  || 0, color: 'var(--sup-against-dot)' },
  ];

  const recentActivity = [...allVoters]
    .filter(v => v.updated_at)
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 5);

  const cardStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-md)',
    padding: 20,
    boxShadow: 'var(--shadow-sm)'
  };

  return (
    <div>
      {/* Welcome Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 6 }}>
          Campaign Dashboard
        </div>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, fontWeight: 600, color: 'var(--text-strong)', margin: 0, letterSpacing: '-0.3px', lineHeight: 1.1 }}>
          Welcome back, {user?.full_name || 'Campaign Manager'}
        </h2>
      </div>

      {/* Hero: Victory ring + Support breakdown */}
      {canSeeVotes && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr', gap: 16, marginBottom: 22 }}>
          {/* Victory ring */}
          <div style={{ ...cardStyle, padding: '24px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: 26 }}>
            <ProgressRing pct={needed > 0 ? (totalSupport / needed) * 100 : 0} color={toWin <= 0 ? '#10b981' : '#3b82f6'}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 42, fontWeight: 700, color: 'var(--text-strong)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {fmtPct(needed > 0 ? Math.min((totalSupport / needed) * 100, 100) : 0)}
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginTop: 5 }}>To Victory</div>
            </ProgressRing>
            <div style={{ flex: 1, minWidth: 0, alignSelf: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <span style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', background: '#3b82f61f', color: '#3b82f6' }}><Icon name="target" size={16} /></span>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Path to Victory</h3>
              </div>
              {[
                { label: 'Your supporters', value: totalSupport,                 color: '#10b981' },
                { label: 'Needed to win',   value: needed,                       color: '#3b82f6' },
                { label: toWin > 0 ? 'Votes to go' : 'Secured by', value: toWin > 0 ? toWin : Math.abs(toWin), color: toWin > 0 ? '#f59e0b' : '#10b981' },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-2)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)' }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: row.color, flexShrink: 0 }} />{row.label}
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-strong)', fontVariantNumeric: 'tabular-nums' }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Support breakdown donut */}
          <div style={{ ...cardStyle, padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', background: '#8b5cf61f', color: '#8b5cf6' }}><Icon name="pie" size={16} /></span>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Support Breakdown</h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <SegmentDonut segments={supportSegments} />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-strong)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{total}</div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 8.5, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-4)', marginTop: 3 }}>Voters</div>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {supportSegments.map(s => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                    <span style={{ color: 'var(--text-2)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
                    <span style={{ color: 'var(--text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{s.value}</span>
                    <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', width: 40, textAlign: 'right' }}>{fmtPct(total > 0 ? s.value / total * 100 : 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Inline drill-down panel */}
      {drillDown && (() => {
        const SUPPORTERS = ['Strong Support', 'Leaning Support'];
        let list = allVoters, title = 'All Voters', searchState = { showAll: true };
        if (drillDown === 'supporters') { list = allVoters.filter(v => SUPPORTERS.includes(v.support)); title = 'Your Supporters'; searchState = { support: 'Supporters' }; }
        else if (drillDown === 'undecided')  { list = allVoters.filter(v => !v.support || v.support === 'Undecided'); title = 'Undecided Voters'; searchState = { support: 'Undecided' }; }

        const LIMIT = 50;
        const visible = list.slice(0, LIMIT);
        const remaining = list.length - visible.length;
        return (
          <div ref={drillDownRef} style={{ ...cardStyle, padding: 16, marginBottom: 20, scrollMarginTop: 80 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                {title} <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>({list.length})</span>
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => navigate('/search', { state: searchState })}
                  style={{ fontSize: 11, color: 'var(--link)', background: 'none', border: '1px solid var(--border)', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}>
                  View all in Search →
                </button>
                <button onClick={() => setDrillDown(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>
              </div>
            </div>
            {list.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-4)', fontSize: 13 }}>No voters match</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {visible.map(v => {
                  const color = SC_HOME[v.support] || SC_HOME['Undecided'];
                  return (
                    <div key={v.id} onClick={() => navigate(`/voter/${v.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg)', borderRadius: 6, cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--bg)'}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: "'DM Mono', monospace" }}>
                          {v.island}{v.ward ? ` · ${v.ward}` : ''}{v.house_name ? ` · ${v.house_name}` : ''}
                        </div>
                      </div>
                      {canSeeVotes && v.support && (
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: color.bg, color: color.text, whiteSpace: 'nowrap' }}>
                          {v.support}
                        </span>
                      )}
                    </div>
                  );
                })}
                {remaining > 0 && (
                  <div style={{ textAlign: 'center', padding: 8, fontSize: 12, color: 'var(--text-3)' }}>
                    + {remaining} more — <button onClick={() => navigate('/search', { state: searchState })}
                      style={{ background: 'none', border: 'none', color: 'var(--link)', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 12 }}>view all</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}


      {/* Quick Actions + Recent Activity */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>

        {/* Quick Actions */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
            Quick Actions
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            {[
              { to: '/search',     icon: 'search', title: 'Search Voters', sub: 'Find voter profiles', grad: 'linear-gradient(135deg, #3b82f6, #2563eb)', glow: 'rgba(37,99,235,0.35)' },
              { to: '/analytics',  icon: 'chart',  title: 'Analytics',     sub: 'Insights & metrics',  grad: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', glow: 'rgba(124,58,237,0.35)' },
              { to: '/households', icon: 'building', title: 'Households',     sub: 'Household view',       grad: 'linear-gradient(135deg, #10b981, #059669)', glow: 'rgba(5,150,105,0.35)' },
              { to: '/outreach',   icon: 'phone',  title: 'Outreach',       sub: 'Voter outreach',       grad: 'linear-gradient(135deg, #f59e0b, #d97706)', glow: 'rgba(217,119,6,0.35)' },
            ].map(({ to, icon, title, sub, grad, glow }) => (
              <button
                key={to}
                onClick={() => navigate(to)}
                style={{
                  padding: '14px 16px',
                  background: grad,
                  border: 'none',
                  borderRadius: 'var(--r-md)',
                  color: '#fff',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 10px 24px ${glow}`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <span style={{ width: 38, height: 38, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,0.18)', flexShrink: 0 }}>
                  <Icon name={icon} size={19} color="#fff" />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{title}</span>
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 400, opacity: 0.9, marginTop: 2 }}>{sub}</span>
                </span>
                <Icon name="arrow" size={16} color="#fff" style={{ opacity: 0.85, flexShrink: 0 }} />
              </button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
            Recent Updates
          </h3>
          {recentActivity.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '28px 0', fontSize: 13 }}>
              No recent activity
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recentActivity.map((voter, idx) => {
                const color = canSeeVotes ? (SC_HOME[voter.support] || SC_HOME['Undecided']) : SC_HOME['Undecided'];
                return (
                  <div
                    key={idx}
                    onClick={() => navigate(`/voter/${voter.id}`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 11,
                      padding: '10px 12px',
                      background: 'var(--bg)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--r-sm)',
                      cursor: 'pointer',
                      transition: 'background 0.12s ease, border-color 0.12s ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color.dot, flexShrink: 0, boxShadow: `0 0 0 3px ${color.bg}` }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {voter.name}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: "'DM Mono', monospace", marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {voter.island}{canSeeVotes && ` · ${voter.support || 'Undecided'}`}
                      </div>
                    </div>
                    <Icon name="arrow" size={14} color="var(--text-4)" style={{ flexShrink: 0 }} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

